// Keyboard control.
//
// Not only an accessibility feature, though it is one: on a desktop the command bar is seven small
// targets and a mouse, and a key per command is simply a better way to work a dog. The bar already
// tab-navigates because it is made of real buttons, but tabbing to a control in a real-time game is
// not playing it.
//
// The mapping is a PURE FUNCTION from a key to an action, so what a key does is unit-tested without
// a DOM and without synthesising events.

import { COMMANDS, type CommandType } from '../engine/commands'

export type KeyAction =
  | { kind: 'command'; command: CommandType }
  | { kind: 'bark' }
  | { kind: 'pause' }
  | { kind: 'resume' }
  | null

/**
 * Digits map to the command bar in its own order, so the number printed on a button is the key that
 * fires it — a player never has to learn a second mapping. `COMMANDS` is the single source of that
 * order, here as everywhere else.
 */
export function keyHint(command: CommandType): string {
  return String(COMMANDS.indexOf(command) + 1)
}

/**
 * What a key means right now.
 *
 * `screenOpen` matters: with the pause modal or the help up, a stray digit must not command a dog
 * the player cannot see. Only resume is reachable from there, which is also what makes the pause key
 * a toggle rather than a trap.
 */
export function actionForKey(key: string, screenOpen: boolean, started: boolean): KeyAction {
  const k = key.toLowerCase()

  if (k === 'escape' || k === 'p') {
    if (!started) return null
    return screenOpen ? { kind: 'resume' } : { kind: 'pause' }
  }

  // Everything below drives the dog, so it is inert while a screen is up or before the run starts.
  if (screenOpen || !started) return null

  if (k === 'b' || k === ' ' || k === 'spacebar') return { kind: 'bark' }

  const n = Number(k)
  if (Number.isInteger(n) && n >= 1 && n <= COMMANDS.length) {
    return { kind: 'command', command: COMMANDS[n - 1] }
  }
  return null
}

/** True for keys that would otherwise scroll or activate a focused control. */
export function shouldPreventDefault(key: string): boolean {
  return key === ' ' || key.toLowerCase() === 'spacebar'
}
